import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import Hls from 'hls.js';

interface HLSVideoPlayerProps {
    src: string;
    className?: string;
    controls?: boolean;
    autoPlay?: boolean;
    playsInline?: boolean;
    muted?: boolean;
    preload?: 'none' | 'metadata' | 'auto';
    onLoadedMetadata?: (event: React.SyntheticEvent<HTMLVideoElement>) => void;
    onLoadedData?: (event: React.SyntheticEvent<HTMLVideoElement>) => void;
    style?: React.CSSProperties;
    poster?: string;
}

export interface HLSVideoPlayerRef {
    video: HTMLVideoElement | null;
    hls: Hls | null;
}

const HLSVideoPlayer = forwardRef<HLSVideoPlayerRef, HLSVideoPlayerProps>(({
    src,
    className,
    controls = true,
    autoPlay = false,
    playsInline = true,
    muted = false,
    preload = 'metadata',
    onLoadedMetadata,
    onLoadedData,
    style,
    poster
}, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null);

    useImperativeHandle(ref, () => ({
        video: videoRef.current,
        hls: hlsRef.current
    }), []);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        // Очищаем предыдущий HLS instance
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }

        // Проверяем, является ли URL HLS-потоком
        const isHLS = src.includes('.m3u8') || src.includes('playlist.m3u8');

        if (isHLS) {
            // Используем HLS.js для HLS потоков
            if (Hls.isSupported()) {
                const hls = new Hls({
                    enableWorker: true,
                    lowLatencyMode: true,
                    backBufferLength: 90
                });

                hlsRef.current = hls;
                hls.loadSource(src);
                hls.attachMedia(video);

                hls.on(Hls.Events.MANIFEST_PARSED, () => {
                    console.log('HLS manifest parsed, playing video');
                    if (autoPlay) {
                        video.play().catch(console.error);
                    }
                });

                hls.on(Hls.Events.ERROR, (event, data) => {
                    console.error('HLS error:', data);
                    if (data.fatal) {
                        switch (data.type) {
                            case Hls.ErrorTypes.NETWORK_ERROR:
                                console.log('HLS network error, trying to recover...');
                                hls.startLoad();
                                break;
                            case Hls.ErrorTypes.MEDIA_ERROR:
                                console.log('HLS media error, trying to recover...');
                                hls.recoverMediaError();
                                break;
                            default:
                                console.log('HLS fatal error, destroying instance');
                                hls.destroy();
                                break;
                        }
                    }
                });
            } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
                // Нативная поддержка HLS (Safari)
                video.src = src;
                if (autoPlay) {
                    video.play().catch(console.error);
                }
            } else {
                console.error('HLS не поддерживается в этом браузере');
                // Пытаемся воспроизвести как обычное видео
                video.src = src;
            }
        } else {
            // Обычное видео (MP4, WebM, etc.)
            video.src = src;
            if (autoPlay) {
                video.play().catch(console.error);
            }
        }

        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [src, autoPlay]);

    return (
        <video
            ref={videoRef}
            className={className}
            controls={controls}
            autoPlay={autoPlay}
            playsInline={playsInline}
            muted={muted}
            preload={preload}
            onLoadedMetadata={onLoadedMetadata}
            onLoadedData={onLoadedData}
            style={style}
            poster={poster}
        />
    );
});

HLSVideoPlayer.displayName = 'HLSVideoPlayer';

export default HLSVideoPlayer;
